import { useEffect, useState } from 'react';
import { Globe } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/hooks/useAuth';
import { useUserRole } from '@/hooks/useUserRole';
import { refreshTenantQueries } from '@/utils/queryConfig';
import {
  listLiveTenancyCountries,
  switchTenancyCountry,
  type TenancyCountry,
} from '@/utils/liberiaApi';

/** QLS admin only: changes the session's active country without mixing datasets. */
export const CountrySwitcher = () => {
  const { user, isQlsAdmin, countryId, forceSessionCheck } = useAuth();
  const { role } = useUserRole();
  const [countries, setCountries] = useState<TenancyCountry[]>([]);
  const [switching, setSwitching] = useState(false);

  useEffect(() => {
    if (!isQlsAdmin) {
      return;
    }

    let cancelled = false;
    listLiveTenancyCountries()
      .then((rows) => {
        if (!cancelled) {
          setCountries(Array.isArray(rows) ? rows : []);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setCountries([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isQlsAdmin]);

  if (!isQlsAdmin || countries.length === 0) {
    return null;
  }

  const handleSwitch = async (nextCountryId: string) => {
    if (!user || nextCountryId === countryId) {
      return;
    }

    setSwitching(true);
    try {
      await switchTenancyCountry(nextCountryId, {
        user,
        role,
        hasAddressGenerationAccess: true,
      });
      await forceSessionCheck();
      await refreshTenantQueries();
    } finally {
      setSwitching(false);
    }
  };

  return (
    <div className="flex items-center gap-2 min-w-[10rem]">
      <Globe className="h-4 w-4 text-muted-foreground" />
      <Select value={countryId || ''} onValueChange={handleSwitch} disabled={switching}>
        <SelectTrigger className="h-9">
          <SelectValue placeholder="Switch country" />
        </SelectTrigger>
        <SelectContent>
          {countries.map((country) => (
            <SelectItem key={country.id} value={country.id}>
              {country.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};
